import {getNextSequence} from '@/iamteacherpc/utils/sequence.js';
import {ANSWER_TYPE, SURVEY_OPTION_TYPE, TEXT_LENGTH} from '@/common/consts/survey.js';
import {getField, updateField} from 'vuex-map-fields';
import {
    deleteImageActionFactory,
    mutations as imageMutations,
    uploadImageActionFactory,
} from '@/common/store/modules/common/editor/image.js';
import {
    actions as documentActions,
    mutations as documentMutations,
} from '@/common/store/modules/common/editor/document.js';
import {
    actions as questionListActions,
    mutations as questionListMutations,
} from '@/common/store/modules/common/editor/questionList.js';
import {
    actions as questionActions,
    mutations as questionMutations,
} from '@/common/store/modules/common/editor/question.js';
import {
    actions as parentCounselWeekActions,
    createParentCounselWeekQuestion,
    mutations as parentCounselWeekMutations,
} from '@common/store/modules/common/editor/parentCounselWeek.js';
import {REQUEST_STATE} from '@/iamteacherpc/consts/request.js';
import multimediaApi from '@/common/apis/common/multimediaApi.js';
import {getNowDate, toDateFromISOFormat, toISODateFormat, toISOFormat} from '@/common/utils/date.js';
import {convertToLatestSurveyPayload} from '@/common/store/modules/survey/payload.js';
import {getSatisfactionStepText} from '@/iamteacherpc/utils/satisfactionUtil.js';
import {booleanToDigit} from '@/iamteacherpc/utils/convertBoolean.js';
import {isAllowDeny, isAllowEdit} from '@/iamteacherpc/utils/survey.js';
import {ImageManagerForPayload, ImageManagerForRequestInfo} from '@/iamteacherpc/utils/image.js';
import {availableSubQuestions, hasAnswer, isSubTable, makeQuestionNumber, makeQuestions} from '@common/utils/question';
import getValueFromPath from "@common/utils/getValueFromPath";

export const NAMESPACE = 'surveyEditor';

export const GET_PAYLOAD = 'GET_PAYLOAD';
export const GET_REQUEST_INFO = 'GET_REQUEST_INFO';
export const GET_START_QUESTION_NUMBER = 'GET_START_QUESTION_NUMBER';

export const SET_ANSWER_TEXT = 'SET_ANSWER_TEXT';

export const UPLOAD_IMAGE = 'UPLOAD_IMAGE';
export const DELETE_IMAGE = 'DELETE_IMAGE';
export const UPLOAD_QUESTION_IMAGE = 'UPLOAD_QUESTION_IMAGE';
export const DELETE_QUESTION_IMAGE = 'DELETE_QUESTION_IMAGE';
export const UPLOAD_ANSWER_IMAGE = 'UPLOAD_ANSWER_IMAGE';
export const DELETE_ANSWER_IMAGE = 'DELETE_ANSWER_IMAGE';

export const ADD_ANSWER = 'ADD_ANSWER';
export const DELETE_ANSWER = 'DELETE_ANSWER';
export const MOVE_DOWN_ANSWER = 'MOVE_DOWN_ANSWER';
export const MOVE_UP_ANSWER = 'MOVE_UP_ANSWER';

export const COPY_QUESTION = 'COPY_QUESTION';
export const DELETE_QUESTION = 'DELETE_QUESTION';

export const ADD_QUESTION = 'ADD_QUESTION';
export const ADD_SUB_QUESTION = 'ADD_SUB_QUESTION';
export const ADD_PARENT_COUNSEL_WEEK = 'ADD_PARENT_COUNSEL_WEEK';

export const SET_SURVEY_TYPE_DEFAULT = 'SET_SURVEY_TYPE_DEFAULT';
export const SET_SURVEY_TYPE_PARENT_COUNSEL_WEEK = 'SET_SURVEY_TYPE_PARENT_COUNSEL_WEEK';

export const SET_FORM_DATA = 'SET_FORM_DATA';
export const SET_FORM_DATA_FROM_REQUEST_INFO = 'SET_FORM_DATA_FROM_REQUEST_INFO';

export const SET_ARTICLE = 'SET_ARTICLE';

export const SET_REST_DAYS = 'SET_REST_DAYS';

export const MAX_QUESTION = 99;
export const MAX_ANSWER = 99;
export const MAX_SUB_QUESTION = 99;

const store = {
    namespaced: true,

    state() {
        return {
            form: createInitForm(),
            options: {
                stopListChange: false,
            },
            restDays: [],
            savedParentCounselWeekQuestion: null,
        }
    },

    getters: {
        getField,

        [GET_PAYLOAD](state) {
            const imageManager = new ImageManagerForPayload();
            const startQuestionNumber = getStartQuestionNumber(state.form.questions[0]);

            function getQuestions(questions, parentNumber) {
                return questions.map((question, index) => {
                    const questionNumber = makeQuestionNumber(parentNumber, index, startQuestionNumber);

                    return {
                        isUseQuestion: question.isUseQuestion,
                        questionNo: question.questionNo,
                        questionNumber: questionNumber,
                        question: question.question,
                        questionDesc: question.questionDesc,
                        image: imageManager.setOrGetImage(question.image),
                        mandatory: question.mandatory,
                        type: isSubTable(question) ? ANSWER_TYPE.SUB_TABLE : question.type,

                        ...(question.parentQuestion ? {
                            parentQuestionNo: question.parentQuestion.questionNo,
                        } : {}),

                        ...(hasCondition(question) ? {
                            parentAnswerCondition: question.parentAnswerCondition,
                        } : {}),

                        ...(question.type === ANSWER_TYPE.MULTI_CHOICE ? {
                            maxChoice: question.maxChoice,
                        } : {}),

                        ...(question.type === ANSWER_TYPE.TEXT ? {
                            textLength: question.textLength
                        } : {}),

                        ...(question.type === ANSWER_TYPE.TABLE ? {
                            tableColumnLength: question.tableColumnLength
                        } : {}),

                        ...(isSubTable(question) ? {
                            answers: getSatisfactionStepText(question.parentQuestion.tableColumnLength).map((text, index) => ({
                                answerNo: index,
                                answerNumber: '' + (index + 1),
                                text,
                                image: null,
                            })),
                        } : {}),

                        ...(question.type === ANSWER_TYPE.PARENT_COUNSEL_WEEK_TIME ? {
                            counselPeriods: question.counselPeriods.map(p => toISODateFormat(p.value)),
                            counselTimeSlots: [...question.counselTimeSlots],
                            counselTimes: question.counselTimes.map(period => period.map(timeSlot => ({
                                id: timeSlot.id,
                                value: timeSlot.value,
                            }))),
                            noTimeOption: question.noTimeOption,
                        } : {}),

                        ...(hasAnswer(question) ? {
                            isParentFCFS: question.isParentFCFS,
                            answers: question.answers.map((answer, index) => ({
                                answerNo: answer.answerNo,
                                answerNumber: '' + (index + 1),
                                text: answer.text,
                                image: imageManager.setOrGetImage(answer.image),
                                ...(question.isParentFCFS ? {
                                    limit: answer.limit,
                                    reservedLimit: answer.reservedLimit || 0,
                                } : {}),
                            }))
                        } : {}),

                        questions: (() => {
                            if (!availableSubQuestions({question})) {
                                return [];
                            }
                            if (question.type === ANSWER_TYPE.PARENT_COUNSEL_WEEK) {
                                const questions = getQuestions(question.questions, '');

                                let questionNumber = 1;
                                questions.forEach(question => {
                                    if (question.isUseQuestion) {
                                        question.questionNumber = '' + questionNumber++;
                                    } else {
                                        question.questionNumber = '';
                                    }

                                    if (question.type === ANSWER_TYPE.PARENT_COUNSEL_WEEK_TIME) {
                                        question.questions[0].questionNumber = `${question.questionNumber}-1`;
                                    }
                                });

                                return questions;
                            }
                            if (question.type === ANSWER_TYPE.TABLE) {
                                return question.tableQuestions ? getQuestions(question.tableQuestions, questionNumber) : [];
                            }
                            if (question.isParentFCFS) {
                                return [];
                            }
                            return question.questions ? getQuestions(question.questions, questionNumber) : [];
                        })()
                    };
                })
            }

            return {
                version: 4,
                type: 'SURVEY',
                images: state.form.images.map(image => imageManager.setOrGetImage(image)),
                questions: getQuestions(state.form.questions, ''),
            }
        },

        [GET_REQUEST_INFO](state, getters) {
            const imageManager = new ImageManagerForRequestInfo();

            function getDocuments({documents}) {
                if (!documents) {
                    return [];
                }
                return documents.filter(document => document.uploadState === REQUEST_STATE.SUCCESS)
                    .map(document => {
                        return {
                            type: 'DOCUMENT',
                            fileName: document.fileName,
                            documentNo: document.documentNo,
                            description: null,
                        };
                    });
            }

            return {
                title: state.form.article ? state.form.article.title : state.form.title,
                surveyDesc: state.form.article ? '' : state.form.surveyDesc,
                articleId: state.form.article ? state.form.article.articleId : null,

                startDate: state.form.isUseStartDate ? toISOFormat(state.form.startDate) : '1990-03-01T00:00:00+09:00',
                endDate: toISOFormat(state.form.endDate),
                isAnonymity: state.form.isAnonymity,

                attachments: imageManager.getAttachments(state.form),

                documents: getDocuments({documents: state.form.documents}),
                contents: JSON.stringify(getters[GET_PAYLOAD]),
                questions: makeQuestions(state.form.questions, date => toISODateFormat(date)),

                surveyOptionList: [
                    {
                        optionType: SURVEY_OPTION_TYPE.ALLOW_DENY,
                        optionValue: booleanToDigit(state.form.isAllowDeny),
                    },
                    {
                        optionType: SURVEY_OPTION_TYPE.ALLOW_EDIT,
                        optionValue: booleanToDigit(state.form.isAllowEdit),
                    },
                ],
            };
        },

        [GET_START_QUESTION_NUMBER](state) {
            return getStartQuestionNumber(state.form.questions[0]);
        },
    },

    mutations: {
        updateField,

        ...imageMutations,
        ...documentMutations,
        ...questionListMutations,
        ...questionMutations,
        ...parentCounselWeekMutations,

        setAnswerText(state, {answer, text}) {
            answer.text = text;
        },

        setAnswerImage(state, {answer, image}) {
            const oldImage = answer.image;

            answer.image = {
                ...oldImage,
                ...image
            };
        },

        deleteAnswerImage(state, {answer}) {
            answer.image = null;
        },

        addAnswer(state, {question}) {
            if (question.answers.length >= MAX_ANSWER) {
                return;
            }
            question.answers.push(createAnswer(question));
        },

        deleteAnswer(state, {answer}) {
            const question = answer.question;

            if (question.answers.length < 2) {
                return;
            }

            question.answers = question.answers.filter(a => a !== answer);

            question.questions.forEach(question => {
                question.parentAnswerCondition = question.parentAnswerCondition.filter(c => c !== answer.answerNo);
            });
        },

        moveDownAnswer(state, {answer}) {
            const question = answer.question;
            question.answers = changeAnswerOrder(question.answers, answer, +1);
        },

        moveUpAnswer(state, {answer}) {
            const question = answer.question;
            question.answers = changeAnswerOrder(question.answers, answer, -1);
        },

        copyQuestion(state, {question}) {
            const parent = question.parentQuestion || state.form;
            if (parent.questions.length >= MAX_QUESTION) {
                return;
            }

            function _copyQuestion(question, parentQuestion, parentAnswerNoMap) {
                const newQuestion = {
                    ...question,
                    parentQuestion: parentQuestion,
                    questionNo: getNextSequence(),
                    image: question.image && question.image.uploadState === REQUEST_STATE.SUCCESS ? question.image : null,
                };

                const answerNoMap = new Map();

                newQuestion.answers = newQuestion.answers.map(answer => {
                    const nextSequence = getNextSequence();
                    answerNoMap.set(answer.answerNo, nextSequence);

                    return {
                        ...answer,
                        question: newQuestion,
                        answerNo: nextSequence,
                        image: answer.image && answer.image.uploadState === REQUEST_STATE.SUCCESS ? answer.image : null,
                    };
                });

                newQuestion.parentAnswerCondition = newQuestion.parentAnswerCondition.map(n => parentAnswerNoMap.get(n));
                newQuestion.questions = newQuestion.questions.map(q=> _copyQuestion(q, newQuestion, answerNoMap));
                newQuestion.tableQuestions = newQuestion.tableQuestions.map(q=> _copyQuestion(q, newQuestion, answerNoMap));

                return newQuestion;
            }

            const answerNoMap = new Map();

            if (question.parentQuestion) {
                question.parentQuestion.answers.forEach(answer => {
                    answerNoMap.set(answer.answerNo, answer.answerNo);
                });
            }

            parent.questions = [...parent.questions, _copyQuestion(question, question.parentQuestion, answerNoMap)];
        },

        deleteQuestion(state, {question}) {
            const parent = question.parentQuestion || state.form;
            if (!question.parentQuestion && parent.questions.length < 2) {
                throw new Error('You cant delete a last question');
            }

            if (parent.type === ANSWER_TYPE.TABLE) {
                parent.tableQuestions = parent.tableQuestions.filter(q => q !== question);
            } else {
                parent.questions = parent.questions.filter(q => q !== question);
            }
        },

        addSubQuestion(state, {question, answerNo}) {
            if (question.questions.length >= MAX_SUB_QUESTION) {
                return;
            }

            question.questions.push({
                ...createQuestion(),
                ...(answerNo ? {parentAnswerCondition: [answerNo]} : {}),
                parentQuestion: question,
            });
        },

        addSatisfactionSubQuestion(state, {question}) {
            if (question.tableQuestions.length >= MAX_SUB_QUESTION) {
                return;
            }

            question.tableQuestions.push({
                ...createQuestion(),
                parentQuestion: question
            });
        },

        addQuestion(state) {
            if (state.form.questions.length >= MAX_QUESTION) {
                return;
            }

            state.form.questions.push(createQuestion());
        },

        addParentCounselWeek(state) {
            const hasParentCounselWeek = state.form.questions.some(question => question.type === ANSWER_TYPE.PARENT_COUNSEL_WEEK);
            if (hasParentCounselWeek) {
                return;
            }

            if (state.form.questions.length >= MAX_QUESTION) {
                return;
            }

            state.form.questions.push(createParentCounselWeekQuestion({
                restDays: state.restDays,
            }));
        },

        setSurveyTypeDefault(state) {
            state.savedParentCounselWeekQuestion = state.form.questions.shift();
            if (state.form.questions.length <= 0) {
                state.form.questions.push(createQuestion());
            }
        },

        setSurveyTypeParentCounselWeek(state) {
            if (state.savedParentCounselWeekQuestion) {
                state.form.questions = [state.savedParentCounselWeekQuestion, ...state.form.questions];
            } else {
                state.form.questions = [createParentCounselWeekQuestion({
                    restDays: state.restDays,
                }), ...state.form.questions];
            }

            state.form.isAnonymity = false;
        },

        setFormData(state, {formData}) {
            state.form = formData;
        },

        setFormDataFromRequestInfo(state, {requestInfo}) {
            const questionMap = new Map();

            function getQuestions(questions) {
                return questions.map(question => {

                    let parentQuestion = null;
                    if (question.parentQuestionNo) {
                        parentQuestion = questionMap.get(question.parentQuestionNo) || null;
                    }

                    const newQuestion = {
                        parentQuestion,
                        isUseQuestion: question.isUseQuestion,
                        parentAnswerCondition: question.parentAnswerCondition || [],
                        questionNo: question.questionNo,
                        question: question.question,
                        questionDesc: question.questionDesc,
                        image: question.image && {
                            imageNo: question.image.imageNo,
                            url: question.image.url,
                            uploadState: REQUEST_STATE.SUCCESS,
                        } || null,
                        mandatory: question.mandatory,
                        isParentFCFS: question.isParentFCFS,
                        type: question.type,
                        maxChoice: question.maxChoice || 0,
                        textLength: TEXT_LENGTH.LONG,
                        tableColumnLength: question.tableColumnLength || 5,
                    };

                    questionMap.set(question.questionNo, newQuestion);

                    newQuestion.answers = question.answers && question.answers.map(answer => ({
                        question: newQuestion,
                        answerNo: answer.answerNo,
                        text: answer.text,
                        image: answer.image && {
                            imageNo: answer.image.imageNo,
                            url: answer.image.url,
                            uploadState: REQUEST_STATE.SUCCESS,
                        },
                        limit: question.isParentFCFS ? answer.limit : 0,
                        reservedLimit: question.isParentFCFS ? answer.reservedLimit : null,

                    })) || [
                        createAnswer(newQuestion),
                        createAnswer(newQuestion)
                    ];

                    if (newQuestion.type === ANSWER_TYPE.TABLE) {
                        newQuestion.questions = [];
                        newQuestion.tableQuestions = getQuestions(question.questions || []);
                    } else if (newQuestion.type === ANSWER_TYPE.PARENT_COUNSEL_WEEK_TIME) {
                        newQuestion.counselPeriods = question.counselPeriods.map(isoText => ({
                            id: getNextSequence(),
                            value: toDateFromISOFormat(isoText),
                        }));
                        newQuestion.counselTimeSlots = question.counselTimeSlots;
                        newQuestion.counselTimes = question.counselTimes.map(period => period.map(timeSlot => ({
                            id: timeSlot.id,
                            value: timeSlot.value,
                        })));
                        newQuestion.noTimeOption = question.noTimeOption;
                        newQuestion.questions = getQuestions(question.questions || []);
                    } else {
                        newQuestion.questions = getQuestions(question.questions || []);
                        newQuestion.tableQuestions = [];
                    }

                    return newQuestion;
                });
            }

            function getMainImages({images}) {
                if (!images) {
                    return [];
                }
                return images.map(image => {
                    return {
                        imageNo: image.imageNo,
                        url: image.url,
                        uploadState: REQUEST_STATE.SUCCESS,
                    };
                });
            }

            function getDocuments({documents}) {
                if (!documents) {
                    return [];
                }
                return documents.map(document => {
                    return {
                        documentNo: document.documentNo,
                        fileName: document.fileName,
                        fileSize: document.fileSize,
                        uploadState: REQUEST_STATE.SUCCESS,
                    };
                });
            }

            const payload = convertToLatestSurveyPayload(JSON.parse(requestInfo.contents));

            const formData = createInitForm({
                title: requestInfo.title,
                surveyDesc: requestInfo.surveyDesc,
                images: getMainImages({images: payload.images}),
                documents: getDocuments({documents: requestInfo.documents}),

                isAllowDeny: isAllowDeny({surveyOptionList: requestInfo.surveyOptionList}),
                isAllowEdit: isAllowEdit({surveyOptionList: requestInfo.surveyOptionList}),
                endDate: requestInfo.endDate ? toDateFromISOFormat(requestInfo.endDate) : getInitEndDate(),
                isAnonymity: requestInfo.isAnonymity || false,
                questions: getQuestions(payload.questions),
            });

            state.form = formData;
        },

        setArticle(state, {article}) {
            state.form.article = article;
        },

        setRestDays(state, {restDays}) {
            state.restDays = restDays;
        },
    },

    actions: {
        ...documentActions,
        ...questionListActions,
        ...questionActions,
        ...parentCounselWeekActions,

        updateField({commit, state, dispatch}, {path, value}) {
            commit('updateField', {path, value});
        },

        [UPLOAD_IMAGE]: uploadImageActionFactory({path: 'form'}),
        [UPLOAD_QUESTION_IMAGE]: uploadImageActionFactory({objName: 'question'}),
        [UPLOAD_ANSWER_IMAGE]: uploadImageActionFactory({path: 'answer'}),

        [DELETE_IMAGE]: deleteImageActionFactory({path: 'form'}),
        [DELETE_QUESTION_IMAGE]: deleteImageActionFactory({objName: 'question'}),
        [DELETE_ANSWER_IMAGE]: deleteImageActionFactory({objName: 'answer'}),

        [SET_ANSWER_TEXT]({commit}, {answer, text}) {
            commit('setAnswerText', {answer, text});
        },

        [UPLOAD_ANSWER_IMAGE]({commit, state}, {answer, file}) {
            const image = answer.image;

            if (image && image.uploadState === REQUEST_STATE.PENDING) {
                return;
            }

            commit('setAnswerImage', {answer, uploadState: REQUEST_STATE.PENDING});

            return multimediaApi.getUploadToken()
                .then(tokenResponse => {
                    return multimediaApi.startImageUpload({tokenResponse, file});
                })
                .then(response => {
                    commit('setAnswerImage', {
                        answer,
                        image: {
                            imageNo: response.imageNo,
                            index: 0,
                            url: response.path,
                            uploadState: REQUEST_STATE.SUCCESS
                        }
                    });
                })
                .catch(e => {
                    commit('deleteAnswerImage', {answer});
                    return Promise.reject(e);
                });
        },

        [DELETE_ANSWER_IMAGE]({commit}, {answer}) {
            commit('deleteAnswerImage', {answer});
        },

        [ADD_ANSWER]({commit}, {question}) {
            commit('addAnswer', {question});
        },

        [DELETE_ANSWER]({commit}, {answer}) {
            commit('deleteAnswer', {answer});
        },

        [MOVE_DOWN_ANSWER]({commit}, {answer}) {
            commit('moveDownAnswer', {answer});
        },

        [MOVE_UP_ANSWER]({commit}, {answer}) {
            commit('moveUpAnswer', {answer});
        },

        [COPY_QUESTION]({commit}, {question}) {
            commit('copyQuestion', {question});
        },

        [DELETE_QUESTION]({commit}, {question}) {
            commit('deleteQuestion', {question});
        },

        [ADD_SUB_QUESTION]({commit}, {question, answerNo}) {
            if (question.type === ANSWER_TYPE.TABLE) {
                commit('addSatisfactionSubQuestion', {question});
            } else {
                commit('addSubQuestion', {question, answerNo});
            }
        },

        [ADD_QUESTION]({commit}) {
            commit('addQuestion');
        },

        [ADD_PARENT_COUNSEL_WEEK]({commit}) {
            commit('addParentCounselWeek');
        },

        [SET_SURVEY_TYPE_DEFAULT]({commit}) {
            commit('setSurveyTypeDefault');
        },
        [SET_SURVEY_TYPE_PARENT_COUNSEL_WEEK]({commit}) {
            commit('setSurveyTypeParentCounselWeek');
        },

        [SET_FORM_DATA]({commit}, {formData}) {
            commit('setFormData', {formData});
        },

        [SET_FORM_DATA_FROM_REQUEST_INFO]({commit}, {requestInfo}) {
            commit('setFormDataFromRequestInfo', {requestInfo});
        },

        [SET_ARTICLE]({commit}, {article}) {
            commit('setArticle', {article});
        },

        [SET_REST_DAYS]({commit}, {restDays}) {
            commit('setRestDays', {restDays});
        },
    }
};

/**
 * 설문 초기 폼 데이터 생성
 */
export function createInitForm({
                                   title,
                                   surveyDesc,
                                   images,
                                   documents,
                                   questions,
                                   isAllowDeny,
                                   isAllowEdit,
                                   startDate,
                                   isUseStartDate,
                                   endDate,
                                   isAnonymity,
                                   article,
                               } = {}) {
    return {
        title: title || '',
        surveyDesc: surveyDesc || '',

        images: images || [],
        documents: documents || [],

        questions: questions || createInitQuestions(),

        isAllowDeny: isAllowDeny || false,
        isAllowEdit: isAllowEdit || false,

        isUseStartDate: isUseStartDate || false,
        startDate: startDate || getInitStartDate(),
        endDate: endDate || getInitEndDate(),
        isAnonymity: isAnonymity || false,

        article: article || null,
    };
}

function getInitStartDate() {
    const date = getNowDate();
    date.setHours(date.getHours() + 1);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

/**
 * 신청 종료일 초기 데이터를 반환한다.
 * @returns {Date}
 */
export function getInitEndDate() {
    const date = getNowDate();
    date.setDate(date.getDate() + 1);
    date.setHours(18);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

/**
 * 설문 초기 질문 목록 생성
 */
function createInitQuestions() {
    return [
        createQuestion(),
    ]
}

/**
 * 질문 생성
 */
export function createQuestion() {
    const question = {
        isUseQuestion: true,
        parentQuestion: null,
        parentAnswerCondition: [],
        questionNo: getNextSequence(),
        question: '',
        image: null,
        mandatory: false,
        isParentFCFS: false,
        type: ANSWER_TYPE.ONE_CHOICE,
        maxChoice: 0,
        textLength: TEXT_LENGTH.LONG,
        tableColumnLength: 5,
        answers: [],
        questions: [],
        tableQuestions: [],
    };

    question.answers = [
        createAnswer(question),
        createAnswer(question)
    ];

    return question;
}

/**
 * 답변 생성
 */
export function createAnswer(question) {
    return {
        question: question,
        answerNo: getNextSequence(),
        text: '',
        image: null,
        limit: null,
        reservedLimit: null,
    };
}

export function hasCondition(question) {
    return question.parentQuestion && (question.parentQuestion.type === ANSWER_TYPE.ONE_CHOICE || question.parentQuestion.type === ANSWER_TYPE.MULTI_CHOICE);
}

/**
 * @param answers
 * @param {number} index1
 * @param {number} index2
 */
function changeAnswerOrder(answers, answer, amountOfChange) {
    const index = answers.indexOf(answer);
    if (index + amountOfChange < 0) {
        return answers;
    }

    answers = answers.filter(a => a !== answer);
    answers.splice(index + amountOfChange, 0, answer);

    return answers;
}

function getStartQuestionNumber(parentWeekQuestion) {
    if (parentWeekQuestion.type !== ANSWER_TYPE.PARENT_COUNSEL_WEEK) {
        return 1;
    }

    const parentWeekQuestionCount = parentWeekQuestion.questions.length;
    const notUseQuestionCount = parentWeekQuestion.questions.filter(q => !q.isUseQuestion).length;

    return parentWeekQuestionCount - notUseQuestionCount;
}


export default store;
